// src/pages/SuccessPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MultibancoDetails from './MultibancoDetails';
import { decodePaymentToken } from '../utils/paymentToken';
import './LandingPage.css'; // Reaproveita o mesmo CSS

// O que vem no URL pode ser uma de duas coisas:
//   · o nosso token, quando fomos nós a redirecionar (cartão);
//   · uma referência opaca, quando foi o processador a devolver o cliente
//     (Multibanco, 3DS). Nesse caso o token está guardado no gateway do corp,
//     e é isso que o resolvemos aqui.
//
// Esta troca existe para o processador não ficar com um URL que contém o nome,
// o email e o país do cliente.
const resolverToken = async (valor) => {
  try {
    decodePaymentToken(valor);
    return valor;            // já é um token nosso
  } catch (err) {
    // Referência opaca: o gateway vive na mesma máquina que serve esta página
    const { data } = await axios.get(`${process.env.PUBLIC_URL}/gw/return-ref/${valor}`, { timeout: 8000 });
    return data?.token || null;
  }
};

// Estados possíveis:
// 'checking'  -> a validar com o backend (mostra "Pagamento enviado!")
// 'multibanco'-> pagamento MB pendente, mostramos os dados
// 'confirmed' -> o payment já não existe na DB = foi confirmado e a conta renovada
// 'pending'   -> pagamento ainda em curso (ex: fluxo antigo Paytree)
export default function SuccessPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking');
  const [mbDetails, setMbDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;
    let timer = null;

    // O webhook do Stripe pode demorar uns segundos a chegar ao backend,
    // por isso vamos verificando enquanto o pagamento existir como pendente
    const fetchStatus = async () => {
      try {
        const tokenReal = await resolverToken(token);

        if (cancelled) return;

        if (!tokenReal) {
          // Referência desconhecida ou já não existente: não há nada a mostrar
          // além da mensagem genérica.
          setStatus('pending');
          return;
        }

        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/v2/verify-token`,
          { token: tokenReal },
          { timeout: 8000 }
        );

        if (cancelled) return;

        if (data.multibanco) {
          // Multibanco escolhido: mostrar os dados de pagamento
          setMbDetails(data.multibanco);
          setStatus('multibanco');
          return;
        }

        if (data.payment_link) {
          // Fluxo antigo (Paytree): pagamento ainda pendente lá
          setStatus('pending');
          return;
        }

        if (data.isValid === false) {
          // O payment já não existe na DB -> foi confirmado e a conta renovada
          setStatus('confirmed');
          return;
        }

        // isValid true: o pagamento ainda está pendente (webhook a caminho) -> repetir
        attemptsRef.current += 1;
        if (attemptsRef.current < 10) {
          timer = setTimeout(fetchStatus, 3000);
        } else {
          setStatus('pending');
        }
      } catch (err) {
        // Erro de rede/servidor: mantém a mensagem genérica
        if (!cancelled) setStatus('pending');
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  if (status === 'multibanco' && mbDetails) {
    return <MultibancoDetails details={mbDetails} />;
  }

  if (status === 'confirmed') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Pagamento confirmado!</h2>
          <p className="messageText">
            O seu pagamento foi validado e a sua conta foi renovada.
            Em breve receberá uma notificação com os detalhes.
          </p>
        </div>
      </div>
    );
  }

  // 'checking' e 'pending' partilham a mensagem genérica
  return (
    <div className="pageContainer">
      <div className="messageContainer">
        <h2 className="messageTitle">Pagamento enviado!</h2>
        <p className="messageText">
          Vamos validar o seu pagamento e em breve será notificado.
          Se precisar de suporte, entre em contato.
        </p>

        {/* Volta ao nosso fluxo (com as nossas regras): dados MB se existirem,
            retomar o checkout se ainda estiver pendente, etc. */}
        {token && status === 'pending' && (
          <button
            type="button"
            className="submitButton"
            style={{ marginTop: '15px' }}
            onClick={() => navigate(`/${token}`)}
          >
            Ver dados para pagamento
          </button>
        )}
      </div>
    </div>
  );
}
